import React from 'react'
import { Link } from 'react-router-dom'
import { IAverageProgress, IAveragesFlashcard } from '.'
import DataGrid from '../../../components/DataGrid'
import { Column } from '../../../components/DataGrid/types/types'
import { NA } from '../../../constants'
import { getHumanTime } from '../../../services/utils/date-util'
import { alphanumericCompare } from '../../../services/utils/sort-util'
import { IStudentProgressDetails, ITeacherStoreData } from '../../../stores/teacher.store'
import { ISummaryFilter } from '../SummaryFilters'
import { getFlashcardsCategoricalStudyTimeInSeconds, getFlashcardsCategoricalTotalCardsViewed } from "../../../services/utils/tonp-util"

interface Props {
  selections: ISummaryFilter
  studentsProgress: IStudentProgressDetails[]
  averages: IAveragesFlashcard
}

interface State {
  currentSortField: string
}

let sortMethod = alphanumericCompare
let minSortVal = Number.NEGATIVE_INFINITY
let maxSortVal = Number.POSITIVE_INFINITY
let sortable = true
let columns: Column[] = [
  {
    title: 'FIRST NAME',
    dataProperty: 'studentNameFirst',
    customTooltip: 'Indicates the first name of the student',
    width: '15%',
    sortable,
    sortMethod
  },
  {
    title: 'LAST NAME',
    dataProperty: 'studentNameLast',
    customTooltip: 'Indicates the last name of the student',
    width: '15%',
    sortable,
    sortMethod
  },
  {
    title: 'TOTAL',
    dataProperty: 'studyTimeT',
    customTooltip: 'Indicates the total study time on all flashcards',
    sortable,
    sortMethod
  },
  {
    title: 'ENGLISH',
    dataProperty: 'studyTimeE',
    customTooltip: 'Indicates the study time on English flashcards',
    sortable,
    sortMethod
  },  
  {
    title: 'MATH',
    dataProperty: 'studyTimeM',
    customTooltip: 'Indicates the study time on Math flashcards',
    sortable,
    sortMethod
  },
  {
    title: 'SCIENCE',
    dataProperty: 'studyTimeS',
    customTooltip: 'Indicates the study time on Science flashcards',
    sortable,
    sortMethod
  },
  {
    title: 'SAT VOCAB',
    dataProperty: 'studyTimeSV',
    customTooltip: 'Indicates the study time on SAT Vocab flashcards',
    sortable,
    sortMethod
  },
]

interface ISimplifiedStudentProgress {
    firstName: string
    lastName: string
    emailAddress: string
    timeE: number
    timeM: number
    timeS: number
    timeSV: number
    timeT: number
    isAverage: boolean
    tonp: any[]
}

let averageStudentProgress = {
    id: "?",
    firstName: "",
    lastName: "",
    emailAddress: "",
    timeE: 0,
    timeM: 0,
    timeS: 0,
    timeSV: 0,
    timeT: 0,
    tonp: [],
    isAverage: true
}

export default class RosterFlashcardSummary extends React.Component<Props, State> {
  averageUsers = {}

  constructor(props) {
    super(props)
    this.state = {
      currentSortField: "default"
    }
  }

  createContent = (studentProgress: IStudentProgressDetails | ISimplifiedStudentProgress, index: number) => {
    let {firstName, lastName, isAverage, tonp } = studentProgress
    let timeE = 0
    let timeM = 0
    let timeS = 0
    let timeSV = 0
    let timeT = 0

    if (!isAverage) {
        timeE = getFlashcardsCategoricalStudyTimeInSeconds(tonp, "English")
        timeM = getFlashcardsCategoricalStudyTimeInSeconds(tonp, "Math")
        timeS = getFlashcardsCategoricalStudyTimeInSeconds(tonp, "Science")
        timeSV = getFlashcardsCategoricalStudyTimeInSeconds(tonp,"SATVocab")
        timeT = timeE + timeM + timeS + timeSV 
    }
    else {
        let simplifiedProgress = studentProgress as ISimplifiedStudentProgress
        timeE = simplifiedProgress.timeE
        timeM = simplifiedProgress.timeM
        timeS = simplifiedProgress.timeS
        timeSV = simplifiedProgress.timeSV
        timeT = simplifiedProgress.timeT
    }

    let htE = getHumanTime(timeE, true, true)
    let htM = getHumanTime(timeM, true, true)
    let htS = getHumanTime(timeS, true, true)
    let htSV = getHumanTime(timeSV, true, true)
    let htT = getHumanTime(timeT, true, true)

    let cardViewedE = getFlashcardsCategoricalTotalCardsViewed(tonp, "English")
    let cardViewedM = getFlashcardsCategoricalTotalCardsViewed(tonp, "Math")
    let cardViewedS = getFlashcardsCategoricalTotalCardsViewed(tonp, "Science")
    let cardViewedSV = getFlashcardsCategoricalTotalCardsViewed(tonp,"SATVocab")
    let cardViewedTotal = cardViewedE + cardViewedM + cardViewedS + cardViewedSV
    let id = `roster-lesson-summary-${index}`
    let {averages} = this.props
    let getAverageInfoBasedOnSortField = (currentSortField: string) => {
      let prefix = "Average"
      let averageInfo = ""
      let {averageTimeEnglish, averageTimeMath, averageTimeScience, averageTimeSATVocab, averageTimeTotal} = averages
      let averageHTE = getHumanTime(averageTimeEnglish, true, true)
      let averageHTM = getHumanTime(averageTimeMath, true, true)
      let averageHTS = getHumanTime(averageTimeScience, true, true)
      let averageHTSV = getHumanTime(averageTimeSATVocab, true, true)
      let averageHTT = getHumanTime(averageTimeTotal, true, true)

      switch (currentSortField) {
        case "studyTimeE":
          averageInfo = `<span class="average--val">` + averageHTE.text + `</span>` + prefix + ` Time on English Flashcards`
          averageInfo = averageTimeEnglish !== -1 ? averageInfo: ""
          break
        case "studyTimeM":
          averageInfo = `<span class="average--val">` + averageHTM.text + `</span>` + prefix + ` Time on Math Flashcards`
          averageInfo = averageTimeMath !== -1 ? averageInfo: ""
          break
        case "studyTimeS":
          averageInfo = `<span class="average--val">` + averageHTS.text + `</span>` + prefix + ` Time on Science Flashcards`
          averageInfo = averageTimeScience !== -1 ? averageInfo: ""
          break
        case "studyTimeSV":
          averageInfo = `<span class="average--val">` + averageHTSV.text + `</span>` + prefix + ` Time on SAT Vocab Flashcards`
          averageInfo = averageTimeSATVocab !== -1 ? averageInfo: ""
          break
        case "studyTimeT":
          averageInfo = `<span class="average--val">` + averageHTT.text + `</span>` + prefix + ` Total Time on Flashcards`
          averageInfo = averageTimeTotal !== -1 ? averageInfo: ""
          break
      }
      return averageInfo ? averageInfo : ""
    }
    let {currentSortField} = this.state
    let averageInfo = getAverageInfoBasedOnSortField(currentSortField)
    let getFirstNameOrAverageDetails = () => {
      return () =>
        <>
          { !isAverage ?
            <>
              {firstName}
            </>
            :
            <>
              <span dangerouslySetInnerHTML={{__html: averageInfo}}/>
            </>
          }
        </> 
    }
    let data = {
        studentNameFirst: {
          // We are overloading this with first name or average details (based on the sort field)
          content: getFirstNameOrAverageDetails(),
          comparator: firstName,
        }, 
        studentNameLast: {
          content: lastName,
          comparator: lastName,
        },
        studyTimeE: {
          content: timeE > 0 ? htE.text: NA,
          comparator: timeE > 0 ? timeE: minSortVal,
        },
        studyTimeM: {
          content: timeM > 0 ? htM.text: NA,
          comparator: timeM > 0 ? timeM: minSortVal,
        },
        studyTimeS: {
          content: timeS > 0 ? htS.text: NA,
          comparator: timeS > 0 ? timeS: minSortVal,
        },
        studyTimeSV: {
          content: timeSV > 0 ? htSV.text: NA,
          comparator: timeSV > 0 ? timeSV: minSortVal,
        },
        studyTimeT: {
            content: timeT > 0 ? htT.text: NA,
            comparator: timeT > 0 ? timeT: minSortVal,
        },
    }

    // Let's highlight the average row here (please note if there is no averageInfo then collapse the row!)
    let highlightRowClassName  = ""
    if (isAverage) {
      if (averageInfo !== "") {
        highlightRowClassName = "row-highlight"
      }
      else {
        highlightRowClassName = "row-highlight-collapsed"
      }
    }

    return {
      id,
      data,
      highlightRowClassName,
    }
  }
  
  onSortAllowed = (sortFieldName: string) => {
    let {currentSortField} = this.state
    if (sortFieldName !== currentSortField) {
      setTimeout(() => {
        this.setState({currentSortField: sortFieldName})
      }, 10)
    }
    return true
  }

  getCardBodyContents = (studentsProgress: IStudentProgressDetails[]) => {
    let {averages} = this.props
    let extendedStudentsProgress = [...studentsProgress]
    
    averageStudentProgress.timeE = averages.averageTimeEnglish
    averageStudentProgress.timeM = averages.averageTimeMath
    averageStudentProgress.timeS = averages.averageTimeScience
    averageStudentProgress.timeSV = averages.averageTimeSATVocab
    averageStudentProgress.timeT = averages.averageTimeTotal


    // Add average row data only if there are more than 1 entry
    if (studentsProgress.length > 1) {
      extendedStudentsProgress.push(averageStudentProgress as any)
    }

    let data = extendedStudentsProgress.map((progress, index) => this.createContent(progress, index))
  
    return (
      <DataGrid
        className="lesson-table tbl-1-2-fixed col-1-static"
        columns={columns}
        data={data}
        onSortAllowed={this.onSortAllowed}
        emptyText="No flashcards summary found for students."
      />
    )
  }

  public render() {
    let {studentsProgress} = this.props
    if (!studentsProgress) return null

    let flashcardSummaryDetails = this.getCardBodyContents(studentsProgress)

    return (
      <div className="roster-view-lesson-summary-container">
        <div className="individual-data-section">
        <h2 className="h3">Flashcards Summary</h2>
        </div> 
        <hr/>

        <div className="roster-view-content">
          {flashcardSummaryDetails}
        </div>
      </div>
    )
  }
}
